/**
 * Dorsia Terms of Use and Privacy Policy text content.
 * Scraped from api.dorsia.com/terms and api.dorsia.com/privacy.
 * Last updated: 2026-02-10
 */

export const TERMS_OF_USE = `Terms of Use

Last updated: November 7, 2024

Welcome to Dorsia! Please read this Terms of Use Agreement ("Terms") carefully. These Terms are a legal contract between you and Elomi Corporation ("Dorsia," "we," "us" or "our"). These Terms govern your use, and Dorsia's provision to you, of any Dorsia product or service that contains a link to these Terms, including any website operated by or on behalf of Dorsia with a link to these Terms (collectively, the "Site"), any mobile application offered by Dorsia (the "App"), and any content, information, services, features, or resources available or enabled on the Site or the App (collectively, the "Services").

Your use of the Services may be subject to any additional terms, conditions and policies that we separately post on the Services and any agreements that you have separately executed with Dorsia ("Supplemental Terms") which are incorporated by reference into these Terms, (together, the Agreement"). To the extent there is any conflict between these Terms and any Supplemental Terms, the Supplemental Terms will control with respect to the Services or feature to which the Supplemental Terms relate.

ACCEPTANCE

BY APPLYING FOR A DORSIA MEMBERSHIP, CREATING A DORSIA ACCOUNT, OR OTHERWISE ACCESSING OR USING THE SERVICES, OR ANY PORTION THEREOF, YOU ACKNOWLEDGE AND AGREE THAT: (A) YOU HAVE READ, UNDERSTAND, AND AGREE TO BE BOUND BY THE AGREEMENT; AND (B) YOU ARE NOT A PERSON OR ENTITY BARRED FROM USING THE SERVICES UNDER THE LAWS OF THE UNITED STATES, YOUR PLACE OF RESIDENCE OR ANY OTHER APPLICABLE JURISDICTION.

1. Changes to Terms.

THE AGREEMENT IS SUBJECT TO CHANGE BY DORSIA IN ITS SOLE DISCRETION AT ANY TIME. When changes are made, Dorsia will make a new copy of the Terms available on the Site and, to the extent applicable, in the App, and any new Supplemental Terms will be made available from within, or through, the affected Services. We will also update the "Last Updated" date at the top of the Terms of Use.

2. ACCESS TO THE SERVICES.

2.1 Rights to Use the Services. The Services, and any parts thereof are protected by copyright laws throughout the world. Subject to the Agreement, Dorsia grants you the right to access and use the features and functionality of the Services.

2.2. Application License. For any App made available as part of the Services, Dorsia grants you, subject to your compliance with the Agreement, a limited non-exclusive, non-transferable, non-sublicensable, revocable license to download, install and use a copy of the App on a single mobile device or computer that you own or control.

2.3. Updates. You understand that the Services are evolving. You acknowledge and agree that Dorsia may update the Services with or without notifying you.

2.4. Certain Restrictions. By accessing and using the Services you agree that you will not, and will not permit any third party to: (a) license, sell, rent, or commercially exploit the Services; (b) use any metatags or other "hidden text" using Dorsia's name; (c) frame or utilize framing techniques to enclose any Dorsia content; (d) modify, decompile, or reverse engineer any part of the Services; (e) access or use the Services to build a competitive product; (f) use automated software or scraping tools; and (g) use scraping tools for machine learning or AI training.

2.5. No Support or Maintenance. You acknowledge that Dorsia will have no obligation to provide you with support or maintenance in connection with the Services.

2.6. Ownership. All intellectual property rights in the Services are owned by Dorsia or Dorsia's suppliers.

2.7. Privacy. Dorsia will collect and use your personal information in accordance with its Privacy Policy.

3. MEMBERSHIP; REGISTRATION AND ACCOUNT.

3.1. Membership Application. In order to access certain features, you are required to apply for a Dorsia membership. Dorsia reserves the right to establish eligibility criteria and makes all Membership decisions in its sole discretion.

3.2. Membership Plan. To enjoy full access to the App and Services, you may need to sign up and pay for a subscription ("Membership Fee").

3.3. Registration and Account Creation. If approved, you must create an account and provide certain information including your name, email address and password.

3.4. Account Responsibilities. You are responsible for maintaining the confidentiality of your Account login information and are fully responsible for all activities that occur under your Account.

4. RESERVATIONS.

4.1. Reservations. Once registered, you may make a reservation at a participating restaurant in the Dorsia network. Each Reservation is subject to a minimum amount that you guarantee to spend ("Minimum Spend").

4.2. Confirmation of a Custom Request. Your Custom Request does not constitute a confirmed Reservation. Dorsia shall notify you whether your Custom Request has been accepted.

4.3. Declination of a Custom Request. If a Restaurant declines your request, you shall be free to re-submit.

4.4. Dorsia's Cancellation Policy. If you cancel more than 8 hours prior to the Reservation Time, you shall be credited back the full amount. If you cancel less than 8 hours prior, only 50% will be credited back.

4.5. Dorsia's No-Show Policy. If you do not arrive within 15 minutes after your Reservation Time, you will be considered a "No Show" and the Minimum Spend will be forfeited.

4.6. Other Obligations. You agree not to resell Reservations and will conduct yourself appropriately at any Restaurant.

4.7. Restaurant Failing to Honor Your Reservation. Please contact support if a Restaurant fails to honor your Reservation.

5. FEES AND PURCHASE TERMS.

5.1. Payment. You agree to pay all fees to your Account when due. You must provide a valid credit card as a condition to signing up.

5.2. Subscription Cycle. Each billing cycle is one year. Your subscription automatically renews each year.

5.3. Refunds. Generally, fees are nonrefundable unless specifically communicated otherwise.

5.4. Price Changes. We reserve the right to adjust Membership Fee pricing.

5.5. Cancellation of Subscription. You may terminate your Membership at any time before renewal through account settings.

5.6. Exceeding the Minimum Spend. You will be required to pay any Excess Spend either directly to the Restaurant or to Dorsia.

5.7. Minimum Spend Exclusive of Fees, Taxes, and Gratuity. The Minimum Spend is exclusive of payment processing fees, taxes, and gratuities.

5.8. Stripe. Dorsia uses Stripe as a third-party payment processor. By using the Services, you agree to Stripe's Privacy Policy and Terms of Service.

5.9-5.11. Sales Tax, Withholding Taxes, and Credits. You are responsible for applicable taxes. Credits have no cash value, are not transferable, and expire upon Membership cancellation.

6. FEEDBACK. If you provide Dorsia with feedback, you assign all rights in such Feedback to Dorsia.

7. ACCEPTABLE USE POLICY. You agree not to use the Services for unlawful purposes, distribute malware, send spam, harvest data, or impersonate others.

8. DORSIA COMMUNICATIONS. By providing your phone number or email, you consent to receive communications from Dorsia including operational and promotional messages. You may opt out of promotional emails at any time.

9. INDEMNIFICATION. You agree to indemnify and hold Dorsia harmless from any claims arising from your use of the Services or violation of the Agreement.

10. THIRD-PARTY LINKS AND APPLICATIONS. The Services may contain third-party links. Dorsia is not responsible for third-party content.

11. DISCLAIMERS. THE SERVICES ARE PROVIDED "AS-IS" AND "AS AVAILABLE." DORSIA DISCLAIMS ALL WARRANTIES INCLUDING MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE. DORSIA MAKES NO REPRESENTATIONS ABOUT ANY RESTAURANTS OR THE QUALITY OF DINING EXPERIENCES.

12. LIMITATION ON LIABILITY. DORSIA SHALL NOT BE LIABLE FOR INDIRECT, CONSEQUENTIAL, OR PUNITIVE DAMAGES. OUR TOTAL LIABILITY IS LIMITED TO $100.

13. TERM AND TERMINATION. We may suspend or terminate your rights at any time for any reason.

14. COPYRIGHT POLICY. Dorsia respects intellectual property rights. Contact legal@dorsia.com for copyright claims.

15. ARBITRATION CLAUSE AND CLASS ACTION WAIVER (U.S. RESIDENTS). All disputes will be resolved by binding arbitration. You waive the right to a jury trial and class action participation.

16. GENERAL. These Terms are governed by the laws of Delaware. Contact: 3921 Alton Road, Suite 413, Miami Beach, FL 33140. legal@dorsia.com`;

export const PRIVACY_POLICY = `Privacy Policy

Effective as of August 16, 2024.

This Privacy Policy describes how ELOMI Corporation (d/b/a Dorsia) and our subsidiaries ("Dorsia," "we," "us" or "our") processes personal information that we collect through our digital or online properties or services that link to this Privacy Policy (including our website, mobile application, social media pages, marketing activities, live events and other activities).

Personal information we collect

Information you provide to us:
- Contact data: name, email address, phone number, professional title
- Demographic data: city, state, country of residence, postal code, gender, age
- Membership application data: referral information and application details
- Profile data: username, password, date of birth, biographical details, photograph, social network links, dining preferences
- Reservation data: restaurant visits, dates, times, order details
- Communications data: exchanges with us through the Service or otherwise
- Transactional data: information needed to complete reservations, transaction history
- Payment data: payment card information or bank account number
- Marketing data: preferences for receiving marketing communications

Third-party sources: We may combine your information with data from public sources, private sources, affiliate partners, marketing partners, and third-party services.

Automatic data collection: We automatically log device data, online activity data, precise geolocation data (when authorized), and communication interaction data. Some automatic data collection is facilitated by cookies.

How we use your personal information

- Service delivery and operations: providing and improving the Service, personalizing your experience, maintaining your profile, enabling security features, communicating with you
- Research and development: analyzing and improving the Service, developing new products
- Marketing and advertising: direct marketing communications, interest-based advertising
- Service improvement and analytics: analyzing usage patterns, understanding user activity
- Compliance and protection: complying with laws, protecting rights and safety, preventing fraud

How we share your personal information

We may share your information with:
- Affiliates: our corporate parent, subsidiaries, and affiliates
- Service providers: hosting, IT, customer support, email delivery, marketing, analytics
- Payment processors: such as Stripe for payment processing
- Advertising partners: for interest-based advertising
- Restaurants: contact data and reservation data for your bookings
- Business and marketing partners: event co-sponsors, joint product offerings
- Professional advisors: lawyers, auditors, bankers, insurers
- Authorities: law enforcement and government authorities when required
- Business transferees: in the context of business transactions

We will not share your mobile phone number with third parties for marketing purposes.

Your choices

- Access or update your information through your account or by contacting support
- Opt-out of marketing communications via unsubscribe links or replying STOP to texts
- Control cookies through your browser settings or our Cookie Notice
- Disable precise geolocation access in your device settings
- Delete content or close your account by contacting us

Other sites and services

The Service may contain links to third-party websites. We are not responsible for their actions or privacy practices.

Security

We employ technical, organizational and physical safeguards but cannot guarantee the security of your personal information.

International data transfer

Your personal information may be transferred to the United States or other locations where privacy laws may differ.

Children

The Service is not intended for use by anyone under 18 years of age.

Changes to this Privacy Policy

We reserve the right to modify this Privacy Policy at any time and will notify you of material changes.

How to contact us

Email: legal@dorsia.com
Mail: 3921 Alton Rd Suite 413, Miami Beach, FL 33140

California privacy rights notice

Under California's Shine the Light law, California residents may request information about personal data shared with third parties for direct marketing purposes.

Notice to European Users

If you are located in the EEA or UK, you have additional rights including access, correction, deletion, data portability, restriction of processing, and the right to object. Dorsia is the controller of your personal information. Our UK GDPR representative is EDPO UK Ltd.

Legal bases for processing include contractual necessity, legitimate interests, compliance with law, and consent. We retain personal information as long as necessary to fulfill the purposes for which it was collected.

For concerns, contact your local data protection authority or the UK Information Commissioner's Office.

Last Updated: August 2024`;
